import { useQuery } from 'react-query';

import { GetPersonsResponse } from '../../../../backend/src/case/person/dto/get-persons-response.dto';
import { PersonFilters } from '../../types';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { Person } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface GetMatterPersonsReq {
  matterId: string | null;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
  paginated?: boolean;
  filters?: PersonFilters;
}

// Conditional type for return value based on paginated parameter
type GetMatterPersonsReturnType<T extends GetMatterPersonsReq> = T['paginated'] extends false
  ? ReturnType<typeof useQuery<Person[], Error>>
  : ReturnType<typeof useQuery<GetPersonsResponse, Error>>;

export function useGetMatterPersons<T extends GetMatterPersonsReq>(params: T): GetMatterPersonsReturnType<T> {
  const { matterId, searchQuery, page, pageSize, paginated, filters } = params;
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<GetMatterPersonsReturnType<T>, Error>(
    [QueryEntity.MatterPersons, { matterId }, { page, pageSize, paginated, filters }, { searchQuery }],
    async () => {
      const urlParams = new URLSearchParams();

      function addParam(key: string, value: unknown) {
        if (value === null || value === undefined) return;
        urlParams.append(key, String(value));
      }

      addParam('searchQuery', searchQuery);
      addParam('page', page);
      addParam('pageSize', pageSize);
      addParam('paginated', paginated);

      if (filters?.personType) {
        for (const filter of filters.personType) {
          addParam('personType[]', filter?.value);
        }
      }

      const response = await fetch(
        `${APIBaseChronos}/api/matters/${matterId}/person?${urlParams.toString()}`,
        fetchConfigGET,
      );

      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!matterId,
    },
  ) as GetMatterPersonsReturnType<T>;
}
